import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowDownRight, ArrowUpRight, Minus, RefreshCw } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { API, api, fmtTime } from "@/api";
import { AutoTradingAdminUnlock } from "@/components/AutoTradingAdminUnlock";

const LABELS = { okx: "欧易 OKX", binance: "币安 Binance", gate: "Gate", bybit: "Bybit" };
const MARKS = { okx: "OKX", binance: "BN", gate: "GT", bybit: "BY" };
const n = (v) => Number.parseFloat(v || "0") || 0;
const money = (v) => `${n(v).toFixed(2)} USDT`;

function ExchangeMark({ exchange }) {
  return <span className={`exchange-mark exchange-${exchange}`}>{MARKS[exchange] || "EX"}</span>;
}

function TrendPill({ value }) {
  const amount = n(value);
  if (amount > 0) return <div className="equity-trend up"><span className="trend-icon"><ArrowUpRight size={19} /></span><span>今日上涨</span><b>+{amount.toFixed(2)} USDT</b></div>;
  if (amount < 0) return <div className="equity-trend down"><span className="trend-icon"><ArrowDownRight size={19} /></span><span>今日下跌</span><b>{amount.toFixed(2)} USDT</b></div>;
  return <div className="equity-trend flat"><span className="trend-icon"><Minus size={19} /></span><span>今日持平</span><b>0.00 USDT</b></div>;
}

export default function UserTradingDetailPage() {
  const { userId } = useParams();
  const [auth, setAuth] = useState(null);
  const [authConfigured, setAuthConfigured] = useState(true);
  const [user, setUser] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [trading, setTrading] = useState({ positions: [], waiting_entries: [], history: [], actions: [], follow_sources: [] });
  const [exchange, setExchange] = useState("all");
  const [tab, setTab] = useState("positions");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [usersResult, portfolioResult, tradingResult] = await Promise.all([
        api.get("/managed-users"), api.get(`/managed-users/${userId}/portfolio`),
        api.get(`/managed-users/${userId}/trading`),
      ]);
      setUser((usersResult.data.items || []).find((x) => x.user_id === userId) || null);
      setPortfolio(portfolioResult.data);
      setTrading(tradingResult.data || { positions: [], waiting_entries: [], history: [], actions: [], follow_sources: [] });
      setError("");
    } catch (err) {
      if (err.response?.status === 401) setAuth(false); else setError("交易账户数据加载失败");
    }
  }, [userId]);

  const checkSession = useCallback(async () => {
    try {
      let result = (await api.get("/auto-trading/auth/session")).data;
      setAuthConfigured(result.auth_configured);
      if (!result.authenticated) {
        try { result = (await api.post("/auto-trading/auth/refresh")).data; } catch { /* unlock */ }
      }
      setAuth(Boolean(result.authenticated));
      if (result.authenticated) load();
    } catch { setAuth(false); }
  }, [load]);
  useEffect(() => { checkSession(); }, [checkSession]);
  useEffect(() => {
    if (!auth) return undefined;
    const eventSource = new EventSource(`${API}/managed-users/${userId}/portfolio/stream`, { withCredentials: true });
    eventSource.addEventListener("portfolio", (event) => {
      try { setPortfolio(JSON.parse(event.data)); } catch { /* ignore */ }
    });
    eventSource.onerror = () => { /* browser auto reconnects; REST refresh remains available */ };
    return () => eventSource.close();
  }, [auth, userId, load]);
  useEffect(() => {
    if (!auth) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [auth, load]);

  const login = async (password) => {
    setBusy(true); setError("");
    try { await api.post("/auto-trading/auth/login", { password }); setAuth(true); await load(); }
    catch (err) { setError(err.response?.data?.detail === "INVALID_ADMIN_PASSWORD" ? "管理员密码错误" : "管理员登录失败"); }
    finally { setBusy(false); }
  };

  const configuredExchanges = useMemo(() => (user?.exchanges || []).map((x) => x.exchange), [user]);
  const selected = exchange === "all" ? null : (portfolio?.exchanges || []).find((x) => x.exchange === exchange);
  const singleExchange = exchange !== "all";
  const equity = singleExchange ? (selected?.total_equity_usd ?? "0") : (portfolio?.total_equity_usd ?? "0");
  const cashflowAdjusted = singleExchange ? Boolean(selected?.cashflow_adjusted) : Boolean(portfolio?.cashflow_adjusted);
  const today = singleExchange
    ? (cashflowAdjusted ? selected?.today_pnl_usd : selected?.today_change_usd) ?? "0"
    : (cashflowAdjusted ? portfolio?.today_pnl_usd : portfolio?.today_change_usd) ?? "0";
  const available = singleExchange ? (selected?.available_balance_usd ?? "0") : (portfolio?.available_balance_usd ?? "0");
  const updatedAt = singleExchange ? selected?.updated_at : portfolio?.updated_at;
  const balanceStatus = singleExchange ? selected?.status : ((portfolio?.exchanges || []).some((item) => item.status === "error") ? "partial" : "connected");

  const refresh = async () => {
    if (exchange !== "all") {
      setBusy(true);
      try { await api.post(`/managed-users/${userId}/exchanges/${exchange}/refresh`); await load(); }
      finally { setBusy(false); }
    } else await load();
  };
  const sourceName = (row) => row?.source_display || (row?.source === "gauls" ? "山寨1号机" : row?.source === "unknown" ? "山寨2号机" : "号机");
  const sideName = (value) => String(value || "").toLowerCase() === "short" ? "做空" : "做多";
  const pct = (value) => value == null ? "—" : `${(Number(value) * 100).toFixed(0)}%`;
  const actionName = (row) => (row?.actions || []).join(" / ") || row?.reason || "—";
  const newTradeStatus = (() => {
    if (!user?.trading_enabled) return "交易已关闭";
    if (user?.new_trade_ready) return "新开仓已就绪";
    if (user?.new_trade_block_reason === "USER_SUBSCRIPTION_EXPIRED") return "已到期，禁止新开仓";
    if (user?.new_trade_block_reason === "NO_SIGNAL_SOURCE_SELECTED") return "未选择跟随号机";
    if (user?.new_trade_block_reason === "ACCOUNT_TRADING_VALIDATION_REQUIRED") return "OKX API 待交易验证";
    if (user?.new_trade_block_reason === "PLATFORM_LIVE_NOT_ENABLED") return "平台 Live 未开启";
    return "暂不可新开仓";
  })();

  return <section data-testid="managed-user-detail-page">
    <div className="page-heading"><div><p className="eyebrow">用户交易详情</p><h1>{user?.user_name || "交易详情"}</h1></div>
      <Link className="text-action-link" to="/users"><ArrowLeft size={15} /> 返回用户管理</Link></div>
    {auth === null && <p className="empty-state bordered">正在检查管理员会话…</p>}
    {auth === false && <AutoTradingAdminUnlock busy={busy} error={error} configured={authConfigured} onLogin={login} />}
    {auth && <>
      {error && <p className="error-text">{error}</p>}
      <div className="trade-user-strip"><div><b>{user?.user_name || "—"}</b><span>{user?.uid ? `UID ${user.uid}` : "未填写 UID"}</span></div>
        <span className={`status-badge ${user?.new_trade_ready ? "ok" : "err"}`}>{newTradeStatus}</span></div>
      <div className="trade-exchange-switch"><span>查看交易所</span><button className={exchange === "all" ? "active" : ""} onClick={() => setExchange("all")}>全部交易所</button>
        {configuredExchanges.map((ex) => <button key={ex} className={exchange === ex ? "active" : ""} onClick={() => setExchange(ex)}><ExchangeMark exchange={ex} />{LABELS[ex] || ex}</button>)}</div>
      <div className="equity-grid">
        <div className="equity-card primary"><div className="equity-card-head"><span>当前总权益</span><span>{exchange === "all" ? "全部" : LABELS[exchange]}</span></div>
          <strong>{money(equity)}</strong><TrendPill value={today} /></div>
        <div className={`equity-card ${n(today) >= 0 ? "positive" : "negative"}`}><div className="equity-card-head"><span>{cashflowAdjusted ? "今日盈亏" : "今日变化"}</span><span>USDT</span></div>
          <strong>{n(today) >= 0 ? "+" : ""}{n(today).toFixed(2)}</strong>
          <small>{cashflowAdjusted ? "已扣除已识别的充值 / 提现 / 划转" : "资金流水未完整时按权益变化显示"} · 可用余额 {money(available)}</small></div>
      </div>
      <div className="balance-freshness"><span>最后更新：{fmtTime(updatedAt)} · {balanceStatus === "connected" ? "实时监听中" : balanceStatus === "partial" ? "部分交易所同步异常" : balanceStatus === "error" ? "同步异常" : "等待首次同步"}</span><button className="secondary" onClick={refresh} disabled={busy}><RefreshCw size={14} /> {exchange === "all" ? "刷新页面数据" : "立即校准余额"}</button></div>
      <div className="trade-detail-tabs">
        {[{ key: "positions", label: "当前持仓" }, { key: "waiting", label: "等待入场" }, { key: "history", label: "交易历史" }, { key: "logs", label: "日志" }].map((item) => <button key={item.key} className={tab === item.key ? "active" : ""} onClick={() => setTab(item.key)}>{item.label}</button>)}
      </div>
      {tab === "positions" && <div className="table-frame"><table><thead><tr><th>号机</th><th>币种</th><th>方向</th><th>实际入场</th><th>剩余仓位</th><th>单笔风险</th><th>杠杆</th><th>状态</th></tr></thead><tbody>
        {(trading.positions || []).map((row) => <tr key={row.trade_id}><td>{sourceName(row)}</td><td className="mono strong">{row.symbol}</td><td>{sideName(row.direction)}</td><td className="mono">{row.effective_position_avg_fill_price || row.actual_avg_fill_price || row.current_execution_price || "—"}</td><td>{pct(row.remaining_fraction)}</td><td className="mono">{row.fixed_risk_usdt || "—"} U</td><td className="mono">{row.max_leverage || "—"}x</td><td>{row.status}</td></tr>)}
      </tbody></table>{!(trading.positions || []).length && <p className="empty-state">当前没有自动跟单持仓</p>}</div>}
      {tab === "waiting" && <div className="table-frame"><table><thead><tr><th>号机</th><th>币种</th><th>方向</th><th>入场规则</th><th>等待原因</th><th>风险</th><th>状态</th></tr></thead><tbody>
        {(trading.waiting_entries || []).map((row) => <tr key={row.trade_id}><td>{sourceName(row)}</td><td className="mono strong">{row.symbol}</td><td>{sideName(row.direction)}</td><td>{row.entry_intent || "—"}</td><td>{row.wait_reason || row.reason || "—"}</td><td className="mono">{row.fixed_risk_usdt || "—"} U</td><td>{row.status}</td></tr>)}
      </tbody></table>{!(trading.waiting_entries || []).length && <p className="empty-state">当前没有等待入场</p>}</div>}
      {tab === "history" && <div className="table-frame"><table><thead><tr><th>更新时间</th><th>号机</th><th>币种</th><th>方向</th><th>实际入场</th><th>剩余</th><th>状态</th><th>原因</th></tr></thead><tbody>
        {(trading.history || []).map((row) => <tr key={row.trade_id}><td>{fmtTime(row.updated_at)}</td><td>{sourceName(row)}</td><td className="mono strong">{row.symbol}</td><td>{sideName(row.direction)}</td><td className="mono">{row.effective_position_avg_fill_price || row.actual_avg_fill_price || "—"}</td><td>{pct(row.remaining_fraction)}</td><td>{row.status}</td><td>{row.reason || row.failure_reason || "—"}</td></tr>)}
      </tbody></table>{!(trading.history || []).length && <p className="empty-state">暂无已结束自动跟单记录</p>}</div>}
      {tab === "logs" && <div className="table-frame"><table><thead><tr><th>时间</th><th>号机</th><th>币种</th><th>动作</th><th>状态</th><th>原因</th></tr></thead><tbody>
        {(trading.actions || []).map((row) => <tr key={row.action_id}><td>{fmtTime(row.updated_at || row.created_at)}</td><td>{sourceName(row)}</td><td className="mono strong">{row.symbol}</td><td>{actionName(row)}</td><td>{row.status}</td><td>{row.reason || "—"}</td></tr>)}
      </tbody></table>{!(trading.actions || []).length && <p className="empty-state">暂无仓位执行日志</p>}</div>}
    </>}
  </section>;
}
